import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, Key, MessageSquare, User, Lock, AlertCircle, Loader2, ArrowRight, Copy, Check, Store } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

const Login = () => {
  const [loginType, setLoginType] = useState('owner'); // 'owner' or 'cashier'
  const [step, setStep] = useState(1); // 1=mobile, 2=PIN entry, 3=forgot PIN OTP, 4=show new PIN
  const [forgotPinMode, setForgotPinMode] = useState(false);
  
  // Form fields
  const [mobileNumber, setMobileNumber] = useState('');
  const [pin, setPin] = useState('');
  const [otp, setOtp] = useState('');
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [storeInfo, setStoreInfo] = useState(null);
  const [remainingResets, setRemainingResets] = useState(3);
  
  // PIN display state
  const [generatedPin, setGeneratedPin] = useState('');
  const [showPinDisplay, setShowPinDisplay] = useState(false);
  const [pinCopied, setPinCopied] = useState(false);
  
  // Support contact from centralized settings
  const [supportWhatsApp, setSupportWhatsApp] = useState('+916382947171');
  
  const navigate = useNavigate();

  // Fetch support contact from centralized settings
  useEffect(() => {
    const fetchSupportSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/settings`);
        if (response.data?.support_whatsapp) {
          setSupportWhatsApp(response.data.support_whatsapp);
        }
      } catch (err) {
        // Use default if fetch fails
      }
    };
    fetchSupportSettings();
  }, []);

  // Owner: Check mobile and go to PIN entry
  const handleOwnerCheckMobile = async () => {
    if (mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/login/owner/check-mobile`, {
        mobile_number: mobileNumber
      });
      
      setStoreInfo({
        store_id: response.data.store_id,
        business_name: response.data.business_name,
        has_pin: response.data.has_pin
      });
      setRemainingResets(response.data.remaining_pin_resets || 3);
      setStep(2); // Go to PIN entry
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  // Owner: Login with PIN
  const handleOwnerPinLogin = async () => {
    if (pin.length < 4 || pin.length > 6) {
      setError('PIN must be 4-6 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/login/owner/pin`, {
        mobile_number: mobileNumber,
        pin: pin
      });
      
      localStorage.setItem('pos_token', response.data.token);
      localStorage.setItem('pos_user_type', 'owner');
      navigate('/gposp/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid PIN');
    } finally {
      setLoading(false);
    }
  };

  // Forgot PIN: Send OTP
  const handleForgotPinSendOtp = async () => {
    if (remainingResets <= 0) {
      setError('Monthly PIN reset limit (3) reached. Please contact admin.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/login/owner/forgot-pin`, {
        mobile_number: mobileNumber
      });
      
      setStoreInfo({
        store_id: response.data.store_id,
        business_name: response.data.business_name
      });
      setRemainingResets(response.data.remaining_resets || 0);
      setForgotPinMode(true);
      setStep(3); // Go to OTP entry
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError('Monthly PIN reset limit (3) reached. Please contact admin.');
      } else {
        setError(err.response?.data?.detail || 'Failed to send OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot PIN: Verify OTP and get new PIN
  const handleForgotPinVerify = async () => {
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/login/owner/forgot-pin/verify`, {
        mobile_number: mobileNumber,
        otp: otp
      });
      
      if (response.data.show_pin_info && response.data.generated_pin) {
        setStoreInfo({
          store_id: response.data.store_id,
          business_name: response.data.business_name
        });
        setGeneratedPin(response.data.generated_pin);
        setRemainingResets(response.data.remaining_resets || 0);
        setShowPinDisplay(true);
        setStep(4); // Show new PIN
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Copy PIN to clipboard
  const copyPin = async () => {
    try {
      await navigator.clipboard.writeText(generatedPin);
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy PIN');
    }
  };

  // Continue after seeing new PIN - go back to PIN entry
  const continueToLogin = () => {
    setPin('');
    setOtp('');
    setForgotPinMode(false);
    setShowPinDisplay(false);
    setGeneratedPin('');
    setStep(2); // Go back to PIN entry
  };

  // Cashier: Login with mobile + PIN
  const handleCashierLogin = async () => {
    if (mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (pin.length < 4 || pin.length > 6) {
      setError('PIN must be 4-6 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/login/cashier`, {
        mobile: mobileNumber,
        pin: pin
      });
      
      localStorage.setItem('pos_token', response.data.token);
      localStorage.setItem('pos_user_type', 'cashier');
      navigate('/gposp/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setMobileNumber('');
    setPin('');
    setOtp('');
    setError('');
    setStoreInfo(null);
    setForgotPinMode(false);
    setGeneratedPin('');
    setShowPinDisplay(false);
    setPinCopied(false);
  };

  const openWhatsApp = () => {
    const cleanNumber = supportWhatsApp.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanNumber}?text=Hi, I need help with my GPOS login. My mobile number is ${mobileNumber}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">GPOS</span>
          </h1>
          <p className="text-violet-200/60">Point of Sale System</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 overflow-hidden">
          {/* Tab Selector */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => { setLoginType('owner'); resetForm(); }}
              className={`flex-1 py-4 text-sm font-medium transition-all ${
                loginType === 'owner' 
                  ? 'text-white bg-white/10' 
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              <User className="inline-block mr-2" size={18} />
              Owner Login
            </button>
            <button
              onClick={() => { setLoginType('cashier'); resetForm(); }}
              className={`flex-1 py-4 text-sm font-medium transition-all ${
                loginType === 'cashier' 
                  ? 'text-white bg-white/10' 
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              <User className="inline-block mr-2" size={18} />
              Cashier Login
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Owner Login Flow */}
            {loginType === 'owner' && (
              <>
                {/* Step 1: Enter Mobile Number */}
                {step === 1 && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-white/70 mb-2">Registered Mobile Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                        <input
                          type="tel"
                          value={mobileNumber}
                          onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="Enter 10-digit mobile"
                          className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500"
                          maxLength={10}
                          data-testid="owner-mobile-input"
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
                        <AlertCircle size={16} className="text-red-400" />
                        <p className="text-sm text-red-300">{error}</p>
                      </div>
                    )}

                    <button
                      onClick={handleOwnerCheckMobile}
                      disabled={loading || mobileNumber.length !== 10}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="continue-btn"
                    >
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <ArrowRight size={20} />}
                      Continue
                    </button>
                  </div>
                )}

                {/* Step 2: Enter PIN */}
                {step === 2 && !forgotPinMode && (
                  <div className="space-y-4">
                    {/* Store Info */}
                    {storeInfo && (
                      <div className="p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-xl">
                        <p className="text-emerald-300 font-medium">{storeInfo.business_name}</p>
                        <p className="text-emerald-400/70 text-sm">Store ID: {storeInfo.store_id}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm text-white/70 mb-2">Enter Store PIN</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                        <input
                          type="password"
                          value={pin}
                          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter 6-digit PIN"
                          className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500 text-center tracking-[0.5em] font-mono text-xl"
                          maxLength={6}
                          data-testid="owner-pin-input"
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
                        <AlertCircle size={16} className="text-red-400" />
                        <p className="text-sm text-red-300">{error}</p>
                      </div>
                    )}

                    <button
                      onClick={handleOwnerPinLogin}
                      disabled={loading || pin.length < 4}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="pin-login-btn"
                    >
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <Key size={20} />}
                      Login
                    </button>

                    {/* Forgot PIN Link */}
                    <div className="text-center pt-2">
                      <button
                        onClick={handleForgotPinSendOtp}
                        disabled={loading || remainingResets <= 0}
                        className="text-violet-400 hover:text-violet-300 text-sm underline disabled:opacity-50 disabled:no-underline"
                      >
                        {remainingResets > 0 
                          ? `Forgot PIN? Reset via OTP (${remainingResets} left this month)` 
                          : 'Monthly reset limit reached - Contact admin'}
                      </button>
                    </div>

                    <button
                      onClick={resetForm}
                      className="w-full py-2 text-white/50 hover:text-white/80 text-sm"
                    >
                      ← Change mobile number
                    </button>
                  </div>
                )}

                {/* Step 3: Forgot PIN - Enter OTP */}
                {step === 3 && forgotPinMode && (
                  <div className="space-y-4">
                    {storeInfo && (
                      <div className="p-4 bg-amber-500/20 border border-amber-500/30 rounded-xl">
                        <p className="text-amber-300 font-medium">PIN Reset</p>
                        <p className="text-amber-400/70 text-sm">OTP sent to {mobileNumber}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm text-white/70 mb-2">Enter OTP</label>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500 text-center tracking-[0.5em] font-mono text-xl"
                        maxLength={6}
                        data-testid="forgot-pin-otp-input"
                      />
                    </div>

                    {error && (
                      <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
                        <AlertCircle size={16} className="text-red-400" />
                        <p className="text-sm text-red-300">{error}</p>
                      </div>
                    )}

                    <button
                      onClick={handleForgotPinVerify}
                      disabled={loading || otp.length !== 6}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="verify-otp-btn"
                    >
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <MessageSquare size={20} />}
                      Verify & Get New PIN
                    </button>

                    <button
                      onClick={() => { setForgotPinMode(false); setOtp(''); setStep(2); }}
                      className="w-full py-2 text-white/50 hover:text-white/80 text-sm"
                    >
                      ← Back to PIN login
                    </button>
                  </div>
                )}

                {/* Step 4: Show New PIN */}
                {step === 4 && showPinDisplay && (
                  <div className="space-y-4">
                    {/* Success Header */}
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="text-emerald-400" size={32} />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-1">PIN Reset Successful!</h3>
                      <p className="text-white/60 text-sm">Your new PIN has been generated</p>
                    </div>

                    {/* Store Info Card */}
                    <div className="p-4 bg-violet-500/20 border border-violet-500/30 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Store className="text-violet-300" size={20} />
                        <div>
                          <p className="text-violet-300 font-medium">{storeInfo?.business_name}</p>
                          <p className="text-violet-400/70 text-sm">Store ID: {storeInfo?.store_id}</p>
                        </div>
                      </div>
                    </div>

                    {/* PIN Display Card */}
                    <div className="p-6 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl">
                      <p className="text-emerald-300/70 text-sm text-center mb-3">Your New Login PIN</p>
                      <div className="flex items-center justify-center gap-3">
                        <p className="text-4xl font-mono font-bold text-white tracking-[0.3em]">
                          {generatedPin}
                        </p>
                        <button
                          onClick={copyPin}
                          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
                          title="Copy PIN"
                        >
                          {pinCopied ? (
                            <Check className="text-emerald-400" size={20} />
                          ) : (
                            <Copy className="text-white/70" size={20} />
                          )}
                        </button>
                      </div>
                      {pinCopied && (
                        <p className="text-emerald-400 text-xs text-center mt-2">PIN copied!</p>
                      )}
                    </div>

                    {/* Instruction */}
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                      <p className="text-amber-300 text-sm text-center">
                        <strong>Save this PIN!</strong> Use it to login to your store.
                        {remainingResets > 0 && (
                          <span className="block text-xs mt-1 text-amber-400/70">
                            {remainingResets} PIN reset(s) remaining this month
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Continue Button */}
                    <button
                      onClick={continueToLogin}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-700 hover:to-purple-700 flex items-center justify-center gap-2"
                      data-testid="continue-to-login-btn"
                    >
                      <ArrowRight size={20} />
                      Continue to Login
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Cashier Login Flow */}
            {loginType === 'cashier' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Registered Mobile Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit mobile"
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      maxLength={10}
                      data-testid="cashier-mobile-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">PIN</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                    <input
                      type="password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter your PIN"
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      maxLength={6}
                      data-testid="cashier-pin-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
                    <AlertCircle size={16} className="text-red-400" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleCashierLogin}
                  disabled={loading || mobileNumber.length !== 10 || pin.length < 4}
                  className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="cashier-login-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <ArrowRight size={20} />}
                  Login
                </button>

                {/* Forgot PIN for Cashier */}
                <div className="text-center pt-2">
                  <p className="text-white/50 text-sm">
                    Forgot PIN? Contact your store owner.
                  </p>
                </div>
              </div>
            )}

            {/* Support Link - Only show on step 1 and 2 */}
            {(step === 1 || step === 2) && !forgotPinMode && (
              <div className="pt-4 border-t border-white/10">
                <button
                  onClick={openWhatsApp}
                  className="w-full flex items-center justify-center gap-2 py-3 text-white/60 hover:text-white/90 transition-colors"
                >
                  <MessageSquare size={16} className="text-green-400" />
                  <span className="text-sm">Need Help? Contact Support</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Register Link */}
        <p className="text-center text-white/50 mt-6 text-sm">
          Don't have an account?{' '}
          <a href="/gposp/register" className="text-violet-400 hover:text-violet-300 font-medium">
            Register your business
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
